import { ENTITY_TYPES } from "@/services/entityTypes"
import { PERMISSIONS } from "@/services/permission"
import { PROJECT_FORM_NAMES } from "@/store/project"

// The file browser's view of the eight entity types that carry attachments.
//
// Every one of them already has an identical set of file endpoints — `/{path}/file/download/...`,
// `/view/...`, `POST /{path}/file/{ownerId}`, `DELETE ...` — which is why the browser needs no
// download or view endpoint of its own: it builds those URLs from `path` and calls the same
// controllers the entity's own "Dateien" tab calls.
//
// Icon, label and route come from `services/entityTypes.js`, the one registry that answers how a
// backend entity type is named, pictured and navigated to on this client — the command palette and a
// record's reference list read the same entries.
//
// Adding a type is a backend change (`DomainFileQueries.SPECS` + `FrontendFileBrowserService`'s two
// permission maps) plus one entry here.

/**
 * The eight types, each as the shared registry describes it plus the two things only the browser
 * needs: the API path segment its file endpoints live under, and what it takes to change files there.
 *
 * The project entry is the one exception to taking `routeOf` verbatim — the browser links into a
 * project's *Dateien* tab rather than its default one, since that is what the row is about.
 */
export const FILE_TYPES = {
  PROJECT: {
    ...ENTITY_TYPES.PROJECT,
    path: "project",
    routeOf: id => ({ name: "ProjectDetail", params: { projectId: id }, hash: `#${PROJECT_FORM_NAMES.FILES}` }),
    editPermissions: [PERMISSIONS.EDIT_ASSIGNED_PROJECT_FILE, PERMISSIONS.EDIT_FOREIGN_PROJECT_FILE],
  },
  CUSTOMER: {
    ...ENTITY_TYPES.CUSTOMER,
    path: "customer",
    editPermissions: [PERMISSIONS.EDIT_CUSTOMER_FILE],
  },
  USER: {
    ...ENTITY_TYPES.USER,
    path: "user",
    editPermissions: [PERMISSIONS.EDIT_USER_FILE],
  },
  MACHINE: {
    ...ENTITY_TYPES.MACHINE,
    path: "machine",
    // Machines, materials and additional efforts have no file-specific permission: their attachment
    // controllers gate on the entity's GENERAL flag, so this mirrors that rather than inventing one.
    editPermissions: [PERMISSIONS.EDIT_MACHINE_GENERAL],
  },
  MATERIAL: {
    ...ENTITY_TYPES.MATERIAL,
    path: "material",
    editPermissions: [PERMISSIONS.EDIT_MATERIAL_GENERAL],
  },
  ADDITIONAL_EFFORT: {
    ...ENTITY_TYPES.ADDITIONAL_EFFORT,
    path: "additionaleffort",
    editPermissions: [PERMISSIONS.EDIT_ADDITIONAL_EFFORT_GENERAL],
  },
  CONSTRUCTION_REPORT: {
    ...ENTITY_TYPES.CONSTRUCTION_REPORT,
    path: "constructionreport",
    editPermissions: [
      PERMISSIONS.EDIT_ASSIGNED_PROJECT_CONSTRUCTIONREPORT,
      PERMISSIONS.EDIT_FOREIGN_PROJECT_CONSTRUCTIONREPORT,
    ],
  },
  HOURLY_WAGE_REPORT: {
    ...ENTITY_TYPES.HOURLY_WAGE_REPORT,
    path: "hourlywagereport",
    editPermissions: [
      PERMISSIONS.EDIT_ASSIGNED_PROJECT_HOURLYWAGEREPORT,
      PERMISSIONS.EDIT_FOREIGN_PROJECT_HOURLYWAGEREPORT,
    ],
  },
}

/**
 * What a folder row stands for. Mirrors the backend's `FileFolderKind`.
 *
 * Only `OWNER` names a record. `TYPE` and `SECTION` are named after the *kind* of thing they hold,
 * and the server sends them with an empty title on purpose — that name is a translation this client
 * owns, and a German string from the server would be the one label on screen ignoring the user's
 * language. `folderTitle` is where that is filled in.
 */
export const FOLDER_KIND = {
  TYPE: "TYPE",
  OWNER: "OWNER",
  SECTION: "SECTION",
}

/**
 * What a listing can be ordered by. `NAME`, `MODIFIED` and `SIZE` mirror the backend's `FileSortKey`
 * and are applied there.
 *
 * `LOCATION` is **client-only** and exists only while searching, where the whole result set is on
 * this side and sorting it is therefore exact. The server cannot offer it: the folder of a report hit
 * is its project's name, which is resolved from a lookup after the query rather than read from the
 * document, so there is no field for Mongo to sort on. Search keeps selecting its rows by
 * [SERVER_SORT_FOR] and this only arranges the ones that came back.
 */
export const SORT_KEYS = {
  NAME: "NAME",
  MODIFIED: "MODIFIED",
  SIZE: "SIZE",
  DUE: "DUE",
  LOCATION: "LOCATION",
}

/**
 * Which files a listing admits, by where they stand against their due date. The three states mirror
 * the backend's `FileDueState`; [ALL] is this client's way of saying "no filter" and is never sent.
 *
 * The three **partition** the files that carry a due date, so a file is in exactly one of them and a
 * file with no date is in none. They are the states the row already draws differently — red, amber,
 * plain — rather than a list of questions someone might ask, which is what keeps them from
 * overlapping: [EXPIRED] and [EXPIRING] are things a file is in *instead of* each other.
 *
 * There is deliberately no combined "bald ablaufend & abgelaufen" entry. It sat here once, above a
 * "Nur abgelaufen" that was one of its halves, and the two read as alternatives while one contained
 * the other — the word "nur" carrying the whole explanation of how they related.
 *
 * Applied server-side for the same reason the ordering is: the listing is paged, so filtering the
 * fifty rows this client happens to hold would answer "of this page, which expire" rather than
 * "which expire".
 *
 * It narrows **folders as well as files** — under anything but [ALL] a place counts only the files
 * it admits and lists only the records holding one. That is what makes it usable from the root,
 * where no files are listed at all, and what turns walking down under a filter into a drill-down.
 */
export const DUE_FILTERS = {
  /** No filter. Client-only: the request simply omits the parameter. */
  ALL: "ALL",
  /** Past its due date. */
  EXPIRED: "EXPIRED",
  /** Inside its warning lead, not yet past. */
  EXPIRING: "EXPIRING",
  /** Carries a due date and is comfortably beyond the lead. */
  VALID: "VALID",
}

/** The order they are offered in: no filter, then most urgent first. */
export const DUE_FILTER_ORDER = [
  DUE_FILTERS.ALL,
  DUE_FILTERS.EXPIRED,
  DUE_FILTERS.EXPIRING,
  DUE_FILTERS.VALID,
]

/** Whether a value from the URL is one this build knows — anything else is read as no filter. */
export const isKnownDueFilter = value => value in DUE_FILTERS

/** How the filter travels in a request. [DUE_FILTERS.ALL] is the absence of one, so it is omitted. */
export const dueFilterParam = due => (due && due !== DUE_FILTERS.ALL ? due : undefined)

/**
 * The search query a given ordering implies.
 *
 * Sorting by folder pins the server side to "newest first" and arranges those rows here, so toggling
 * that column never re-runs the term — and switching away from it does, because the direction it
 * pinned is not the one the other columns were left on.
 */
export const serverSearchOrder = (key, ascending) =>
  key === SORT_KEYS.LOCATION ? { sort: SORT_KEYS.MODIFIED, ascending: false } : { sort: key, ascending }

/**
 * The order the places appear in, and the only types that *are* places.
 *
 * The two report types are deliberately absent: they hang inside the project they belong to, because
 * a tenant accumulates one report per site per day and listing them as a seventh place produces a
 * flat list of thousands that nobody navigates and no paging fixes.
 */
export const PLACE_ORDER = ["PROJECT", "CUSTOMER", "USER", "MATERIAL", "MACHINE", "ADDITIONAL_EFFORT"]

/** All view permissions that can put *something* in the browser — the drawer entry's gate. */
export const VIEW_FILE_PERMISSIONS = [
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_FILE,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_FILE,
  PERMISSIONS.VIEW_CUSTOMER_FILE,
  PERMISSIONS.VIEW_USER_FILE,
  PERMISSIONS.VIEW_MACHINE_GENERAL,
  PERMISSIONS.VIEW_MATERIAL_GENERAL,
  PERMISSIONS.VIEW_ADDITIONAL_EFFORT_GENERAL,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_CONSTRUCTIONREPORT,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_CONSTRUCTIONREPORT,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEREPORT,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEREPORT,
]

/** Types the server may send that this build does not know — dropped rather than rendered blank. */
export const isKnownFileType = type => type in FILE_TYPES

/** The path of a place, which is simply the type's name — see the backend's `FolderPath`. */
export const placePath = type => type

export const downloadSrc = (type, ownerId, fileId) => `/${FILE_TYPES[type].path}/file/download/${ownerId}/${fileId}`

export const viewSrc = (type, ownerId, fileId) => `/${FILE_TYPES[type].path}/file/view/${ownerId}/${fileId}`

/**
 * Where a new file is posted.
 *
 * The same per-entity controller the eight "Dateien" tabs upload through, which is why the browser
 * needs no upload endpoint of its own — and why the permission that governs it is the one
 * `GET /files/permissions` already reports.
 */
export const uploadSrc = (type, ownerId) => `/${FILE_TYPES[type].path}/file/${ownerId}`

/** Where the record behind a row lives, or null when this build cannot route there. */
export const ownerRoute = row => {
  const config = FILE_TYPES[row?.type]
  if (!config || !row.ownerId) return null
  return config.routeOf(row.ownerId, row.parentId) ?? null
}

/**
 * A folder's label: the record's own name, or the translated name of what the folder holds.
 * @param t the caller's `$t` — this module has no i18n instance of its own.
 */
export const folderTitle = (folder, t) => {
  if (folder?.kind === FOLDER_KIND.OWNER) return folder.ownerTitle
  const config = FILE_TYPES[folder?.type]
  return config ? t(config.labelKey) : (folder?.ownerTitle ?? "")
}

/**
 * A report section's glyph: a folder first, its contents second.
 *
 * These started as the report type's own icon — a clipboard and a stopwatch — which is where they are
 * wrong. A section is the only folder that ever sits in a list next to files, and at 20px a clipboard
 * is a sheet of paper: "Bautagesberichte" read as an attachment someone had uploaded.
 */
const SECTION_ICONS = {
  CONSTRUCTION_REPORT: "mdi-folder-text",
  HOURLY_WAGE_REPORT: "mdi-folder-clock",
}

/**
 * A folder's icon.
 *
 * Everything you can open is folder-shaped except a place, and a place is only ever listed among
 * other places — at the root every row is one, so there is nothing there for it to be confused with.
 */
export const folderIcon = folder => {
  if (folder?.kind === FOLDER_KIND.SECTION) return SECTION_ICONS[folder.type] ?? "mdi-folder"
  if (folder?.kind === FOLDER_KIND.OWNER) return "mdi-folder"
  return FILE_TYPES[folder?.type]?.icon ?? "mdi-folder"
}

/** Whether this client, not the server, has to sort these rows — see [folderTitle]. */
export const isClientLabelled = folder => folder?.kind !== FOLDER_KIND.OWNER
